import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import { showApiError } from "../../services/apiError";
import UploadIcon from "../../images/UploadIcon";
import {
  getSalesLedger,
  getSalesLedgerPDF,
  getSalesLedgerXLS,
  getTransactionMonthLedger,
} from "../../services/ledger.service";
import { getLocations } from "../../services/locations.services";
import PDFIcon from "../../images/PDFIcon";
import XLSIcon from "../../images/XLSIcon";
import { useResponsive } from "../../hook/useResponsive";
import PurchasesLedgerIcon from "../../images/PurchasesLedgerIcon";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  cells,
} from "../../components/list";

interface SaleLedgerRow {
  Tran: string | number;
  SaleId?: string;
  "Transaction Month": string;
  Reference: string | null;
  NET: string;
  TAX: string;
  Total: string;
  buyer?: string;
  Client?: string | null;
  location?: string;
}

const toAmount = (value: any): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

/** Rows show "#123"; the API matches the bare transaction number. */
const ledgerSearch = (search: string) => String(search ?? "").trim().replace(/^#/, "");

const SalesLedger = () => {
  const companyName = localStorage.getItem("companyName");
  const [listMonths, setListMonths] = useState<any>([]);
  const [country, setCountry] = useState<any[]>([]);
  const [total, setTotal] = useState<any>({});
  const preLanguage = localStorage.getItem("language");
  const companyId = localStorage.getItem("companyId");
  const userId = localStorage.getItem("userId");
  const [showSave, setShowSave] = useState<boolean>(true);
  const { isTouchLayout } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // The query behind the rows on screen, so Download exports what is listed.
  const lastQuery = useRef<ListQuery | null>(null);
  const inforTable: any = {
    img: <PurchasesLedgerIcon />,
    title:
      language && language?.["No sales ledger yet"]
        ? language?.["No sales ledger yet"]
        : "No sales ledger yet",
    desc:
      language && language?.[" Your sale records will appear here once you start adding invoices."]
        ? language?.[" Your sale records will appear here once you start adding invoices."]
        : " Your sale records will appear here once you start adding invoices.",
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  const formatDate = (dateString: any) => {
    if (dateString == null) return "";
    // API may already send "MM/YYYY" (e.g. "02/2026") for Transaction Month
    const s = String(dateString).trim();
    if (/^\d{1,2}\/\d{4}$/.test(s)) return s;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return s || "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const formatDateTime = (dateString: any) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };
  const getListTransactionMonth = async () => {
    try {
      const response: any = await getTransactionMonthLedger(companyId);
      setListMonths(response.data);
    } catch (error) {
      console.error("Error loading terms:", error);
    }
  };
  const getListContry = async () => {
    try {
      const response: any = await getLocations(userId, companyId, "", "", "");
      setCountry(response.data.data);
    } catch (error) {
      console.error("Error loading terms:", error);
    }
  };
  const fetchLedger = async (query: ListQuery) => {
    lastQuery.current = query;
    const response = await getSalesLedger(
      query.page,
      query.limit,
      ledgerSearch(query.search),
      companyId,
      query.filters.period,
      query.filters.location,
    );
    return { rows: response.data.data ?? [], count: response.data.count ?? 0, total: response.data.total };
  };
  useEffect(() => {
    getListTransactionMonth();
    getListContry();
    // ListScreen refetches the rows itself on newToken.
    const handleNewToken = () => {
      getListTransactionMonth();
      getListContry();
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);

  const handleExport = async (type: string) => {
    const filters = lastQuery.current?.filters ?? {};
    const search = ledgerSearch(lastQuery.current?.search ?? "");
    webLoadingTrue();
    try {
      const response =
        type === "pdf"
          ? await getSalesLedgerPDF(
            companyId,
            filters.period,
            filters.location,
            search,
          )
          : await getSalesLedgerXLS(
            companyId,
            filters.period,
            filters.location,
            search,
          );
      const base64Data =
        type === "pdf" ? response?.data?.pdfBase64 : response?.data?.base64;
      const dataUri =
        type === "pdf"
          ? `data:application/pdf;base64,${base64Data}`
          : `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`;
      const link = document.createElement("a");
      link.href = dataUri;
      link.download =
        type === "pdf"
          ? `SalesLedger_${companyName}_${formatDateTime(new Date())}.pdf`
          : `SalesLedger_${companyName}_${formatDateTime(new Date())}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error loading terms:", error);
      showApiError(error, { language });
    } finally {
      webLoadingFalse();
    }
  };

  const saleUrl = (row: SaleLedgerRow) => `/${preLanguage || ""}/sales/${row.SaleId}`;

  const columns: ListColumn<SaleLedgerRow>[] = [
    {
      key: "tran",
      label: "Tran",
      width: 90,
      hideable: false,
      value: (r) => "#" + r.Tran,
      render: (r, ctx) =>
        r.SaleId ? (
          <a
            href={saleUrl(r)}
            onClick={(e) => {
              e.preventDefault();
              webNavigate(saleUrl(r));
            }}
            className="font-inter"
            style={{ fontSize: "14px", fontWeight: 500, color: "inherit", textDecoration: "underline" }}
          >
            {"#" + r.Tran}
          </a>
        ) : (
          cells.text((row: SaleLedgerRow) => "#" + row.Tran)(r, ctx)
        ),
      mobileRender: (r) => "#" + r.Tran,
      mobile: "title",
    },
    {
      key: "month",
      label: "Transaction Month",
      width: 150,
      value: (r) => formatDate(r["Transaction Month"]),
      mobile: "subtitle",
    },
    {
      key: "reference",
      label: "Reference",
      width: 160,
      value: (r) => r.Reference ?? "",
      mobile: "sheet",
    },
    {
      key: "client",
      label: "Client",
      width: 200,
      value: (r) => r.Client ?? r.buyer ?? "",
      mobile: "subtitle",
    },
    {
      key: "location",
      label: "Location",
      width: 160,
      value: (r) => r.location ?? "",
      mobile: "sheet",
    },
    {
      key: "net",
      label: "NET",
      width: 110,
      align: "right",
      render: cells.money((r: SaleLedgerRow) => toAmount(r.NET)),
      mobile: "sheet",
    },
    {
      key: "tax",
      label: "TAX",
      width: 110,
      align: "right",
      render: cells.money((r: SaleLedgerRow) => toAmount(r.TAX)),
      mobile: "sheet",
    },
    {
      key: "total",
      label: "Total",
      width: 120,
      align: "right",
      render: cells.money((r: SaleLedgerRow) => toAmount(r.Total)),
      mobileRender: cells.money((r: SaleLedgerRow) => toAmount(r.Total), true),
      mobile: "trailing",
    },
  ];

  const filters: ListFilter[] = [
    {
      key: "period",
      label: "Period",
      kind: "select",
      options: (Array.isArray(listMonths) ? listMonths : []).map((month: any) => ({
        value: String(month.id),
        label: `${month.transactionYear} - ${month.monthSequence}`,
      })),
    },
    {
      key: "location",
      label: "Location",
      kind: "select",
      options: (country ?? []).map((loc: any) => ({
        value: String(loc.id),
        label: loc.name,
      })),
    },
  ];

  // Desktop links the Tran cell; the phone card opens the record sheet instead.
  const mobileRowActions: ListRowAction<SaleLedgerRow>[] = [
    {
      key: "open",
      label: "Open sale",
      sheetIcon: "sales",
      hidden: (r) => !r.SaleId,
      onRun: (r) => webNavigate(saleUrl(r)),
    },
  ];

  return (
    <div
      className="h-full relative "
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: isTouchLayout ? "auto" : undefined,
        paddingBottom: isTouchLayout ? "24px" : undefined,
      }}
    >
      <div className="">
        <div className="lg:flex items-center justify-between">
          <div className="">
            <p
              style={{ color: "#222525" }}
              className="text-[24px] leading-[130%] font-medium font-inter"
            >
              {language && language?.["Sales Ledger"]
                ? language?.["Sales Ledger"]
                : "Sales Ledger"}
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div
              style={{
                padding: "10px 24px",
                backgroundColor: "#EBF0F0",
                borderRadius: "40px",
                width: "fit-content",
                marginTop: isTouchLayout ? "20px" : "",
              }}
              className="items-center justify-center w-fit cursor-pointer relative button_ledger"
              onClick={() => {
                setShowSave(!showSave);
              }}
            >
              <div className="w-full flex items-center gap-x-2">
                <UploadIcon />
                <p
                  style={{ color: "#0F7679", fontWeight: "500" }}
                  className="text-[14px] leading-[20px] text-main font-inter"
                >
                  {language && language?.["Download"]
                    ? language?.["Download"]
                    : "Download"}
                </p>
              </div>
              {!showSave && (
                <div
                  className={`absolute  bg-white shadow-extend rounded-lg transition-all duration-700 ease-in-out p-1 space-y-2
                                ${showSave ? "opacity-0 translate-y-2 pointer-events-none" : "opacity-100 translate-y-0 pointer-events-auto"}`}
                  style={{
                    width: "225px",
                    height: "92px",
                    // Stacked header on touch: the button sits at the left edge, so open rightwards.
                    left: isTouchLayout ? "0" : undefined,
                    right: isTouchLayout ? undefined : "0",
                    top: "65px",
                    zIndex: 200,
                  }}
                >
                  <div
                    onClick={() => handleExport("pdf")}
                    className="flex items-center gap-x-2 hover:bg-[#D1E4E5] duration-300 cursor-pointer"
                    style={{ borderRadius: "8px", padding: "8px 10px" }}
                  >
                    <PDFIcon />
                    <p className="text-[14px] leading-[20px] text-main font-inter">
                      {language && language?.["Download PDF"]
                        ? language?.["Download PDF"]
                        : "Download PDF"}
                    </p>
                  </div>
                  <div
                    onClick={(e: any) => {
                      handleExport("xls");
                    }}
                    className="flex items-center gap-x-2 hover:bg-[#D1E4E5] duration-300 cursor-pointer"
                    style={{ borderRadius: "8px", padding: "8px 10px" }}
                  >
                    <XLSIcon />
                    <p className="text-[14px] leading-[20px] text-main font-inter">
                      {language && language?.["Download XLS"]
                        ? language?.["Download XLS"]
                        : "Download XLS"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={` items-center transition-all duration-1000 ease-in-out flex `}
          style={{ gap: "24px", marginTop: "24px", flexWrap: "wrap" }}
        >
          <div
            className="rounded-xl bg-white"
            style={{
              width: isTouchLayout ? "100%" : "calc(33% - 12px)",
              padding: "16px",
              border: "1px solid #EBF0F0",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["NET"] ? language?.["NET"] : "NET"}
            </p>
            <div className="flex items-center justify-between">
              <p
                className="text-[24px] text-main font-inter"
                style={{ fontWeight: "600" }}
              >
                {(total?.net ?? 0).toFixed(2)} €
              </p>
            </div>
          </div>
          <div
            className="rounded-xl bg-white"
            style={{
              width: isTouchLayout ? "100%" : "calc(33% - 12px)",
              padding: "16px",
              border: "1px solid #EBF0F0",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["TAX"] ? language?.["TAX"] : "TAX"}
            </p>
            <div className="flex items-center justify-between">
              <p
                className="text-[24px] text-main font-inter"
                style={{ fontWeight: "600" }}
              >
                {(total?.tax ?? 0).toFixed(2)} €
              </p>
            </div>
          </div>
          <div
            className="rounded-xl bg-white"
            style={{
              width: isTouchLayout ? "100%" : "calc(33% - 12px)",
              padding: "16px",
              border: "1px solid #EBF0F0",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["Total + TAX"]
                ? language?.["Total + TAX"]
                : "Total + TAX"}
            </p>
            <div className="flex items-center justify-between">
              <p
                className="text-[24px] text-main font-inter"
                style={{ fontWeight: "600" }}
              >
                {(total?.total ?? 0).toFixed(2)} €
              </p>
            </div>
          </div>
        </div>
      </div>
      <ListScreen
        language={language}
        columns={columns}
        fetchRows={fetchLedger}
        onLoaded={(result: any) => setTotal(result?.total ?? {})}
        rowKey={(r: SaleLedgerRow) => String(r.SaleId ?? r.Tran)}
        searchPlaceholder="Search reference or #"
        searchInputRef={searchInputRef}
        filters={filters}
        mobileRowActions={mobileRowActions}
        countNoun="transactions"
        emptyState={inforTable}
      />
    </div>
  );
};

export default SalesLedger;
